import { Dropdown, IButtonStyles, IconButton, IDropdownOption, IDropdownStyles, IIconProps, Label, Link, Separator, Stack, Text } from '@fluentui/react';
import { RootState } from 'app/store';
import { AppLinkToExternal, textProp } from 'global/Application';
import { EHideReason } from 'global/Visibility/EHideReason';
import { IVisibilityScope } from 'global/Visibility/IVisibilityScope.type';
import {
    bcbs_config_optionsetitem as IXrmTypes_bcbs_config_optionsetitem
} from 'model/CRM';
import {
    Ibcbs_config_optionsetitem_extended as IXrmTypesExtended_Ibcbs_config_optionsetitem_extended
} from 'model/CRM.type';
import {
    IEntityReferenceValue as FAM_IEntityReferenceValue,
    IFlexibleField as FAM_IFlexibleField,
    IGroupConfigurationConfig as FAM_IGroupConfigurationConfig,
    IRef as FAM_IRef,
    IReferenceValue as FAM_IReferenceValue
} from 'model/FlexibleAttributeModel';
import React, { useMemo, useState } from 'react';
import { ControlDebugRenderVisibility } from './ControlDebug';
import InformationCallout from './InformationCallout';
import { $oset_$vision_product_type_$healthy_vision_17030507a93dec118c6100224803edb9_id } from 'model/ConfigOptionSetConstants';

interface IProps {
    [key: string]: any;
    fieldValueOnChange?: (event: any, value: FAM_IEntityReferenceValue | null) => void;
    handleChange?: any;
    defaultFieldValue?: FAM_IReferenceValue;
    field?: FAM_IFlexibleField;
    config: FAM_IGroupConfigurationConfig;
    state?: RootState;
    visibility?: IVisibilityScope;
    disabled?: boolean;
    label?: string;
    placeholder?: string;
    defaultSelectedKey?: any;

}




export const getOptionsetItems = (
    config: FAM_IGroupConfigurationConfig,
    configRef: FAM_IRef,
    props: Pick<IProps, 'visibility' | 'state' | 'field'>,
    nSelectedKeys?: any[],
) => {

    const isAdmin = props.state?.status?.debug?.admin ?? false;
    const isDiagnostic = props.state?.status?.debug?.diagnosis ?? false;

    const options: IDropdownOption<IXrmTypes_bcbs_config_optionsetitem>[] = [];
    const optionLookup: Record<string, IDropdownOption<IXrmTypes_bcbs_config_optionsetitem>> = {};

    const optionSetId = config.cache.map_bcbs_config_attribute[configRef.id]._bcbs_config_optionsetid_value
    const visibilityScope = props.visibility ?? {} as IVisibilityScope;

    if (!optionSetId)
        return { options, optionLookup };

    // todo move this to cache
    config.cache.map_bcbs_config_optionsetitems_bySetId[optionSetId]?.forEach(eachOptionSetItem => {

        const eachOptionSetItemExtended = eachOptionSetItem as IXrmTypesExtended_Ibcbs_config_optionsetitem_extended;

        let disabledReason = IsOptionSetItemDisabled(eachOptionSetItemExtended, visibilityScope);

        const option: IDropdownOption<IXrmTypes_bcbs_config_optionsetitem> = {
            id: eachOptionSetItem.bcbs_config_optionsetitemid!,
            key: eachOptionSetItem.bcbs_config_optionsetitemid!,
            text: eachOptionSetItem.bcbs_name!,
            data: eachOptionSetItem,
            disabled: disabledReason !== 0,
        };

        (option as any).disabledReason = disabledReason;

        if (isAdmin && option.disabled) {
            option.text = `${option.text} [Admin Override]`;
            option.disabled = false;
        }

        option.hidden = option.disabled;

        if (isDiagnostic)
            option.hidden = false;

        options.push(option);
        optionLookup[eachOptionSetItem.bcbs_config_optionsetitemid!] = option;
    });

    // if this is a standalone option, then only one option can be selected
    if (nSelectedKeys) {

        let standaloneOptionSelected = false;

        for (const t of nSelectedKeys) {
            const optionItem = optionLookup[t.id];
            if (optionItem && optionItem.data!.bcbs_isstandalone) {
                standaloneOptionSelected = true;
                break;
            }
        }

        if (standaloneOptionSelected) {
            options.forEach(option => {
                if (!option.data?.bcbs_isstandalone) {
                    option.disabled = true;
                }
            });
        }
    }

    return { options, optionLookup };

}

export const IsOptionSetItemDisabled = (
    eachOptionSetItem: IXrmTypesExtended_Ibcbs_config_optionsetitem_extended,
    visibilityScope: IVisibilityScope
) => {
    const attributeVisibility = eachOptionSetItem.visibility;

    const attribute_operative_effectiveDate = attributeVisibility?.effectiveDate ?? 0;
    const attribute_operative_ExpirationDate = attributeVisibility?.expirationDate ?? 0;
    const attribute_effective_effectiveDate = attributeVisibility?.effective_effectiveDate ?? 0;
    const attribute_effective_expirationDate = attributeVisibility?.effective_expirationDate ?? 0;

    let disabled = EHideReason.Visible;

    // Effective Effective Date
    if (attribute_effective_effectiveDate > 0 && visibilityScope.effectiveDate > 0 && attribute_effective_effectiveDate > visibilityScope.effectiveDate)
        disabled = disabled | EHideReason.EffectiveDate;
    else if (attribute_operative_effectiveDate > 0 && visibilityScope.operativeDate > 0 && attribute_operative_effectiveDate > visibilityScope.operativeDate)
        disabled = disabled | EHideReason.EffectiveDate;

    else
        disabled = disabled & ~(EHideReason.EffectiveDate);

    // Effective Expiration Date
    if (attribute_effective_expirationDate > 0 && visibilityScope.effectiveDate > 0 && attribute_effective_expirationDate < visibilityScope.effectiveDate)
        disabled = disabled | EHideReason.ExpirationDate;
    else if (attribute_operative_ExpirationDate > 0 && visibilityScope.operativeDate > 0 && attribute_operative_ExpirationDate < visibilityScope.operativeDate)
        disabled = disabled | EHideReason.ExpirationDate;

    else
        disabled = disabled & ~(EHideReason.ExpirationDate);

    // Funding Type
    if (attributeVisibility?.fundingType)
        if (visibilityScope.fundingType !== null)
            if (!attributeVisibility?.fundingType?.[visibilityScope.fundingType])
                disabled = disabled | EHideReason.fundingType;

            else
                disabled = disabled & ~(EHideReason.fundingType);

    // Grand Fathered
    // apply only if applicable:
    // for some benefit categories e.x. Vision, if it is set to not enabled grandfather, this check needs to ignore
    if (attributeVisibility?.grandFathered)
        if (visibilityScope.grandFathered !== null)
            if (!attributeVisibility?.grandFathered?.[visibilityScope.grandFathered])
                disabled = disabled | EHideReason.grandFathered;

            else
                disabled = disabled & ~(EHideReason.grandFathered);

    // Erisa
    if (attributeVisibility?.erisa)
        if (visibilityScope.erisa !== null)
            if (!attributeVisibility?.erisa?.[visibilityScope.erisa])
                disabled = disabled | EHideReason.erisa;

            else
                disabled = disabled & ~(EHideReason.erisa);

    // lineOfBusiness
    if (attributeVisibility?.lineOfBusiness)
        if (visibilityScope.lineOfBusiness !== null)
            if (!attributeVisibility?.lineOfBusiness?.[visibilityScope.lineOfBusiness])
                disabled = disabled | EHideReason.lineOfBusiness;

            else
                disabled = disabled & ~(EHideReason.lineOfBusiness);

    if (!visibilityScope?.product) {
        if (eachOptionSetItem.bcbs_config_optionsetitemid === $oset_$vision_product_type_$healthy_vision_17030507a93dec118c6100224803edb9_id) {
            disabled = disabled | EHideReason.Product;
        }
    }
    return disabled;
}

// TODO rename this to OptionSetControl
const OptionsetControl: React.FunctionComponent<IProps> = (props) => {
    const [selectedKey, setSelectedKey] = useState(props.defaultFieldValue ? props.defaultFieldValue.id : props.defaultSelectedKey ?? props.defaultValue);

    const options = useMemo(() => {
        if (props.field) {

            const { options, optionLookup } = getOptionsetItems(props.config, props.field.configRef, props);

            return options;
        }

        console.warn('Option set for the field not found!', props.field);

        return null;
    }, [
        props?.state?.status?.debug?.admin,
        props?.state?.status?.debug?.diagnosis
    ]);

    const style = useMemo(() => {

        const dropDownStyles: Partial<IDropdownStyles> = {
            dropdownOptionText: {
                overflow: 'visible',
                whiteSpace: 'normal',
                wordBreak: 'break-word'
            },
            dropdownItem: {
                height: 'auto',
                minHeight: '36px',
                paddingTop: '6px',
                paddingBottom: '6px'
            },
            dropdownItemSelected: {
                height: 'auto',
                minHeight: '36px',
                paddingTop: '6px',
                paddingBottom: '6px'
            },
        };

        const dropDownOptionItemDiagnosis: React.CSSProperties = {
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
        }

        const dropDownOptionItem: React.CSSProperties = {
            width: '100%',
            height: '100%'
        }

        const iconButtonProps: IIconProps = {
            iconName: 'Clear',
            styles: {
                root: {
                    fontSize: 'x-small',
                    padding: '5px',
                }
            }
        }

        const iconButtonStyles: IButtonStyles = {
            root: {
                height: '100%',
            }
        }

        return {
            dropDown: {
                styles: dropDownStyles,
                optionItemDiagnosis: dropDownOptionItemDiagnosis,
                OptionItem: dropDownOptionItem,
            },
            iconButton: {
                props: iconButtonProps,
                styles: iconButtonStyles
            },
        };
    }, [])


    const fieldValueOnChange = (
        event: React.FormEvent<HTMLDivElement> | null,
        option?: IDropdownOption<IXrmTypes_bcbs_config_optionsetitem> | null,
        index?: number | undefined
    ) => {
        if (option) {
            const value: FAM_IEntityReferenceValue = {
                type: 'entity',
                entityType: 'bcbs_config_optionsetitem',
                id: option.data!.bcbs_config_optionsetitemid!,
                name: option.data!.bcbs_name!,
            }
            props.fieldValueOnChange?.(event, value);
            setSelectedKey(option.key);
        } else {
            props.fieldValueOnChange?.(event, null);
            setSelectedKey(null);
        }
    }

    if (props.disabled)
        return <div style={textProp.root.style}>
            <Label styles={textProp.label}>{props.label}</Label>
            <Text styles={textProp.textStyles}>{props.defaultFieldValue?.name ?? '---'}</Text>
        </div>;

    return (
        <Dropdown
            placeholder={props.placeholder}
            label={props.label}
            options={options ?? []}
            styles={style.dropDown.styles}
            disabled={props.disabled}
            title={props.defaultFieldValue?.name}
            onRenderItem={(scopeProps, defaultRender) => {
                const defaultRenderElement = defaultRender?.(scopeProps);

                const disabledReason = (scopeProps as any)?.disabledReason as EHideReason;

                if (props.state?.status?.debug?.diagnosis) {
                    return (
                        <div
                            style={style.dropDown.optionItemDiagnosis}
                            key={scopeProps?.key}
                        >
                            <InformationCallout>
                                <Stack>
                                    <Link
                                        href={AppLinkToExternal({
                                            type: 'bcbs_config_optionsetitem',
                                            id: scopeProps?.data.bcbs_config_optionsetitemid!
                                        })}
                                        target={"_blank"}
                                        rel={"noopener noreferrer"}
                                    >
                                        ⚙️ OptionSet Item
                                    </Link>
                                    <Separator />
                                    {ControlDebugRenderVisibility(disabledReason)}
                                </Stack>
                            </InformationCallout>
                            {defaultRenderElement}
                            {/* {scopeProps?.title && <span style={{ fontSize: '10px', color: '#666' }}>{scopeProps.title}</span>} */}
                        </div>
                    );
                }

                return (
                    <div style={style.dropDown.OptionItem}
                        key={scopeProps?.key}
                    >
                        {defaultRenderElement}
                    </div>
                );
            }}
            onRenderCaretDown={(scopeProps, defaultRender) => {
                const defaultRenderElement = defaultRender?.(scopeProps);

                return <>
                    {/* <Stack horizontal verticalAlign={"center"}></Stack> */}
                    <IconButton
                        iconProps={style.iconButton.props}
                        styles={style.iconButton.styles}
                        onClick={(event) => {
                            event.stopPropagation();
                            fieldValueOnChange(null, null);
                        }}
                    />
                    {/* <Icon
                        iconName={"ChevronDown"}
                        styles={{
                            root: {
                                color: "rgb(96, 94, 92)",
                                "&:hover": {
                                    fontWeight: 800
                                }
                            }
                        }}
                        onClick={(event) => event?.currentTarget?.parentNode?.onClick!}
                    /> */}
                    {defaultRenderElement}
                </>;

            }}
            //@ts-ignore
            // defaultSelectedKey={props.defaultFieldValue ? props.defaultFieldValue.id : props.defaultSelectedKey ?? props.defaultValue}
            selectedKey={selectedKey}
            onChange={fieldValueOnChange}
            multiSelect={props.multiSelect}
            {...props.other}
        />
    );
}

export default OptionsetControl;

